import { SearchRegistry } from '../types';

export const developerHubSearchEntries: SearchRegistry = {
  '/internal-developer-hub/overview': {
    keywords: [
      'internal developer hub',
      'developer hub',
      'dev hub',
      'overview',
      'developer overview',
      'engineering hub',
      'internal docs',
      'internal developer workspace',
      'ryd internal',
      'developer role',
      'quick access dev',
      'explore by workflow',
      'developer landing',
    ],
    questions: [
      'Where is the internal developer hub overview?',
      'How do I navigate the engineering hub?',
      'Which dev-hub pages need the Developer role?',
    ],
    faqs: [
      {
        q: 'Why can I see the dev hub menu but not enter individual pages?',
        a: 'The Developer Hub overview is open to the `ryd` user group, but most child pages (Automated Tests, Developer Tools, Odoo, UI Settings) require the additional Developer role. Ask a partner admin to add the role if you need write access.',
      },
    ],
  },
  '/internal-developer-hub/api-docs': {
    keywords: [
      'api documentation',
      'api docs',
      'openapi',
      'swagger',
      'service swagger docs',
      'service docs',
      'backend service docs',
      'service catalog',
      'user service api',
      'user service swagger',
      'try out api',
      'gitlab swagger spec',
    ],
    questions: [
      'Where is the OpenAPI documentation of the backend services?',
      'How do I try out a backend service API from the portal?',
      'Where is the User Service Swagger page?',
      'Which version of a service spec am I looking at?',
    ],
    faqs: [
      {
        q: 'Why can I read the documentation but not send requests?',
        a: 'Everyone in the ryd group can read every service. Sending requests needs the service role for this environment: `api-gateway-<env>-read-<service>` for read requests and `api-gateway-<env>-write-<service>` for all requests. Only operations documented in the current version can be called, and every call is recorded in the audit log.',
      },
      {
        q: 'How fresh is the documentation?',
        a: 'Opening a service checks its GitLab repository for a newer spec, at most once every 15 minutes per service. A newer version shows a banner you can load; older versions stay available in the version picker.',
      },
    ],
  },
  '/internal-developer-hub/api-docs/manage': {
    keywords: [
      'manage api documentation',
      'add service swagger',
      'api docs admin',
      'try-out roles',
      'grant api try out role',
      'gitlab spec path',
      'hard sync swagger',
    ],
    questions: [
      'How do I add a backend service to the API documentation?',
      'How do I grant someone the right to try out a service API?',
      'How do I force a refresh of a service spec?',
    ],
  },
  '/internal-developer-hub/usage-overview': {
    keywords: [
      'usage overview',
      'traffic',
      'rate limits',
      'api usage',
      'ai agent usage',
      'data volume',
      'mcp usage',
      'who consumes most',
    ],
    questions: [
      'How much traffic does each company or AI agent cause?',
      'Which partners would hit the planned rate limits?',
      'How much data did the backends return this week?',
    ],
  },
  '/internal-developer-hub/api-docs/audit-log': {
    keywords: [
      'try-out audit log',
      'api try out history',
      'who called api',
      'swagger try out audit',
      'api docs audit',
    ],
    questions: [
      'Who tried out which API call from the API documentation?',
      'How do I see the request and response of a try-out call?',
    ],
  },
  '/internal-developer-hub/services/high-mobility': {
    keywords: [
      'high mobility',
      'high mobility swagger',
      'high mobility api',
      'fleet telemetry swagger',
      'connected vehicle analytics',
      'vehicle telemetry endpoints',
      'high mobility service docs',
      'high mobility openapi',
    ],
    questions: [
      'Where is the High Mobility Swagger page?',
      'How do I open the High Mobility API documentation?',
      'Which page documents the High Mobility telemetry endpoints?',
    ],
  },
  '/internal-developer-hub/services/order-service': {
    keywords: [
      'order service',
      'order service swagger',
      'order service api',
      'order service openapi',
      'order endpoints',
      'order search api',
      'ongoing orders api',
      'orders count api',
      'order service proxy',
    ],
    questions: [
      'Where is the Order Service Swagger page?',
      'How do I call the Order Service API from the portal?',
      'Why do some Order Service endpoints return a gateway error?',
    ],
    faqs: [
      {
        q: 'Why can I only execute some Order Service endpoints?',
        a: 'The rp-users backend proxy forwards every endpoint of the specification, but not all Order Service APIs are enabled at the ryd API Gateway yet — those calls return a gateway error. Sending requests also requires a role: `order_service_api_reader` for read requests (GET) and `order_service_api_executor` for mutating requests (POST, PUT, PATCH, DELETE).',
      },
    ],
  },
  '/internal-developer-hub/developer-tools/api-tester': {
    keywords: [
      'api tester',
      'api playground',
      'request tester',
      'test api requests',
      'developer tools',
      'http tester',
      'try api',
      'curl tester',
      'request builder',
      'response viewer',
    ],
    questions: [
      'Where do I test API requests in the portal?',
      'How do I open the internal API tester?',
      'How do I try a rydpay endpoint without leaving the portal?',
    ],
  },
  '/internal-developer-hub/developer-tools/api-doc-updater': {
    keywords: [
      'api doc updater',
      'doc updater',
      'documentation updater',
      'api docs maintenance',
      'doc tools',
      'edit api doc',
      'doc rebuild',
      'doc generator',
    ],
    questions: ['How do I update an API doc from the portal?', 'Where is the API doc updater?'],
  },
  '/internal-developer-hub/developer-tools/ryd-portal-api-documentation': {
    keywords: [
      'portal api documentation',
      'portal api docs',
      'ryd portal api',
      'api documentation',
      'internal api docs',
      'rp-integrations docs',
      'rp-users docs',
      'portal backend docs',
    ],
    questions: ["Where is the documentation for the portal's own backend APIs?"],
  },
  '/internal-developer-hub/developer-tools/feedback-attachments-guide': {
    keywords: [
      'feedback attachments',
      'feedback guide',
      'attachments guide',
      'support attachments',
      'feedback tooling',
      'feedback uploads',
      'attachment limits',
      'screenshot upload',
    ],
    questions: [
      'How do I attach files to internal feedback requests?',
      'Where is the feedback attachments guide?',
      'What is the maximum attachment size?',
    ],
  },
  '/internal-developer-hub/automated-tests/overview': {
    keywords: [
      'automated tests overview',
      'automated tests',
      'test platform overview',
      'api test overview',
      'connect test overview',
      'odoo test overview',
      'portal test overview',
      'webapp test overview',
      'how to trigger automated tests',
      'how to monitor automated tests',
    ],
    questions: [
      'Where is the automated tests overview page?',
      'What are the 5 automated test suites and what do they test?',
      'How do I trigger and monitor automated tests?',
    ],
  },
  '/internal-developer-hub/automated-tests/trigger-builder': {
    keywords: [
      'ai prompt builder',
      'webhook trigger builder',
      'ai triggers',
      'claude triggers',
      'slack test triggers',
      'flow ids',
      'zero user key',
      'automated tests triggers',
      'curl webhook triggers',
    ],
    questions: [
      'Where is the AI Prompts & Webhook Trigger Builder page?',
      'How do I generate a Claude AI prompt or cURL webhook to trigger a test?',
      'What are the backend flow IDs for all test suites?',
    ],
  },
  '/internal-developer-hub/automated-tests/connect-test-automation': {
    keywords: [
      'connect test automation',
      'connect tests',
      'webview tests',
      'webview automation',
      'happy path tests',
      'fraud rule tests',
      'playwright integration tests',
      'automated tests',
    ],
    questions: [
      'How do I run connect tests?',
      'Where is the connect test automation dashboard?',
      'How to trigger staging webview integration tests?',
    ],
  },
  '/internal-developer-hub/automated-tests/api-test-automation': {
    keywords: [
      'api test automation',
      'api tests',
      'rydpay api tests',
      'backend api tests',
      'fuelling order tests',
      'places contract tests',
      'functional api tests',
      'e2e api tests',
      'automated tests',
    ],
    questions: [
      'How do I run backend rydpay API tests?',
      'Where is the API test automation dashboard?',
      'How to trigger staging fuelling order scenario tests?',
    ],
  },
  '/internal-developer-hub/automated-tests/webapp-test-automation': {
    keywords: [
      'webapp test automation',
      'webapp tests',
      'browser tests',
      'login tests',
      'postpay tests',
      'prepay tests',
      'wash tests',
      'webapp regression',
      'playwright webapp tests',
      'automated tests',
    ],
    questions: [
      'How do I run Webapp tests?',
      'Where is the Webapp test automation dashboard?',
      'How do I trigger staging login, fuelling, or car wash browser journeys?',
    ],
  },
  '/internal-developer-hub/automated-tests/ryd-portal': {
    keywords: [
      'ryd portal test automation',
      'portal tests',
      'portal frontend tests',
      'predefined user tests',
      'fleet manager tests',
      'partner admin tests',
      'page data verification',
      'portal api contract tests',
      'automated tests',
    ],
    questions: [
      'How do I run ryd portal automated tests?',
      'Where is the ryd portal test automation dashboard?',
      'How to test predefined user logins and page data verifications?',
    ],
  },
  '/internal-developer-hub/automated-tests/odoo-test-automation': {
    keywords: [
      'odoo test automation',
      'odoo tests',
      'invoice tests',
      'invoice pdf verification',
      'aggregated billing tests',
      'queue.message',
      'manual queue message',
      'fleet invoice tests',
      'moloni tests',
      'poland fiscal tests',
      'gift card tests',
      'automated tests',
    ],
    questions: [
      'How do I run Odoo invoicing tests?',
      'Where can I see the request body a test sent to Odoo?',
      'How do I send a manual queue.message to Odoo?',
      'How do I verify aggregated fleet invoice PDFs?',
    ],
  },
  '/internal-developer-hub/odoo/getting-started': {
    keywords: [
      'odoo',
      'odoo getting started',
      'odoo intro',
      'odoo onboarding',
      'erp',
      'fleet entity data',
      'odoo overview',
      'odoo concepts intro',
      'odoo developer guide',
    ],
    questions: [
      'How do I get started with Odoo as a ryd developer?',
      'Where is the Odoo developer overview?',
    ],
  },
  '/internal-developer-hub/odoo/key-concepts': {
    keywords: [
      'odoo key concepts',
      'odoo concepts',
      'odoo model',
      'res.partner',
      'res.users',
      'sale order',
      'invoice',
      'fleet entity',
      'core entities',
      'operation types',
      'CREATE_ORDER',
      'UPDATE_ORDER',
      'CORRECT_ORDER',
      'UPLOAD_RECEIPT',
      'idempotency',
      'deduplication',
      'payload hashing',
      'transitory data management',
      'deadline-based data readiness',
      'event logging',
      'audit trail',
    ],
    questions: [
      'What are the key concepts in Odoo for ryd?',
      'Which Odoo models do we use for fleet entities?',
      'What operation types does the Odoo REST API support?',
      'How does Odoo idempotency work via payload hashing?',
    ],
    faqs: [
      {
        q: 'Which Odoo operation types can I send via the REST API?',
        a: 'CREATE_ORDER, UPDATE_ORDER, CORRECT_ORDER and UPLOAD_RECEIPT. Each is documented on the Key Concepts page; idempotency is enforced through payload hashing so retries with the same payload do not create duplicates.',
      },
      {
        q: 'What is "transitory data" in Odoo and why does it exist?',
        a: 'Transitory data lets ryd send incomplete order information with a deadline by which the order must be finalised. It supports flows where some fields (e.g. fuelled volume) are only known after the customer leaves the pump; the deadline triggers automatic completion or invalidation.',
      },
    ],
  },
  '/internal-developer-hub/odoo/rest-api/introduction': {
    keywords: [
      'odoo rest api',
      'odoo rest introduction',
      'rest api odoo',
      'odoo http api',
      'rest endpoints odoo',
      'odoo modern api',
      'delivery note',
      'create_delivery_note',
      'delivery note and invoice',
      'receiptLines',
      'send_email',
      'invoice email',
      'credit note email',
    ],
    questions: [
      'How does the Odoo REST API work?',
      'Which Odoo documents are emailed?',
      'Where is the Odoo REST introduction?',
      'Which flags create a delivery note or an invoice?',
    ],
  },
  '/internal-developer-hub/odoo/rest-api/api-reference': {
    keywords: [
      'odoo rest api reference',
      'odoo rest endpoints',
      'odoo http endpoints',
      'odoo api reference',
      'rest schema odoo',
      'odoo openapi',
      'odoo swagger',
    ],
    questions: [
      'Where is the Odoo REST API reference?',
      'Which Odoo REST endpoints are available?',
    ],
  },
  '/internal-developer-hub/odoo/rest-api/transitory-data': {
    keywords: [
      'transitory data',
      'odoo transitory data',
      'temporary data',
      'transient model',
      'staging data',
      'incomplete order',
      'deadline driven',
      'data readiness',
    ],
    questions: [
      'What is transitory data in Odoo?',
      'How does ryd use transitory data in Odoo flows?',
      'What happens if the deadline expires before transitory data is finalised?',
    ],
  },
  '/internal-developer-hub/odoo/rest-api/idempotency': {
    keywords: [
      'idempotency',
      'odoo idempotency',
      'idempotent requests',
      'idempotency key',
      'duplicate request handling',
      'payload hashing',
      'idempotent retry',
      'safe retry',
      'duplicate detection',
    ],
    questions: [
      'How do I send idempotent requests to Odoo?',
      'Where is the Odoo idempotency contract documented?',
      'How does payload hashing prevent duplicates?',
    ],
  },
  '/internal-developer-hub/odoo/rest-api/order-lifecycle': {
    keywords: [
      'odoo order lifecycle',
      'order lifecycle',
      'sales order lifecycle',
      'invoice lifecycle',
      'odoo flow',
      'sale order states',
      'odoo order states',
      'order draft',
      'order confirmed',
    ],
    questions: [
      'What is the order lifecycle in Odoo?',
      'How does ryd map orders into Odoo states?',
      'When is an Odoo sale order considered confirmed?',
    ],
  },
  '/internal-developer-hub/odoo/xmlrpc-api/introduction': {
    keywords: [
      'odoo xml-rpc',
      'xmlrpc',
      'odoo xmlrpc api',
      'xml rpc introduction',
      'legacy odoo api',
      'xml-rpc endpoints',
      'xml-rpc auth',
    ],
    questions: [
      'How does the Odoo XML-RPC API work?',
      'When should I use XML-RPC instead of REST in Odoo?',
    ],
  },
  '/internal-developer-hub/odoo/xmlrpc-api/odoo-model-explorer': {
    keywords: [
      'odoo model explorer',
      'odoo models',
      'res.partner explorer',
      'inspect odoo models',
      'fields explorer',
      'odoo field types',
      'model browser',
    ],
    questions: ['How do I inspect Odoo models and fields?', 'Where is the Odoo model explorer?'],
  },
  '/internal-developer-hub/discount-management/introduction': {
    keywords: [
      'discount management',
      'discount introduction',
      'partner discounts',
      'discount tools',
      'discount engine',
      'discount overview',
      'rebate engine',
    ],
    questions: [
      'How does ryd discount management work for developers?',
      'Where is the discount-management introduction?',
    ],
  },
  '/internal-developer-hub/discount-management/list-price-guide': {
    keywords: [
      'list price integration',
      'list price guide',
      'list price discount',
      'developer list price',
      'list price implementation',
      'aral list price integration',
      'list price api guide',
    ],
    questions: [
      'How do I integrate list-price discounts as a developer?',
      'Where is the list-price integration guide?',
    ],
  },
  '/internal-developer-hub/discount-management/api-reference': {
    keywords: [
      'discount api reference',
      'discount api',
      'discount endpoints',
      'discount integration api',
      'discount schemas',
      'discount openapi',
    ],
    questions: ['Where is the discount API reference?', 'Which fields define a discount rule?'],
  },
  '/internal-developer-hub/ui-settings/overview': {
    keywords: [
      'ui settings',
      'ui settings api',
      'ui customization',
      'partner ui settings',
      'theming overview',
      'partner branding overview',
      'ui settings overview',
    ],
    questions: ['What is the UI Settings API?', 'How are partner UI settings stored?'],
  },
  '/internal-developer-hub/ui-settings/integration-guide': {
    keywords: [
      'ui settings integration guide',
      'ui settings integration',
      'how to integrate ui settings',
      'consuming ui settings',
      'ui settings client',
      'ui settings cache',
    ],
    questions: ['How do I integrate the UI Settings API into my service?'],
  },
  '/internal-developer-hub/ui-settings/api-reference': {
    keywords: [
      'ui settings api reference',
      'ui settings endpoints',
      'ui settings reference',
      'ui settings swagger',
      'ui settings openapi',
    ],
    questions: ['Where is the UI Settings API reference?'],
  },
};
